import { useEffect, useMemo, useRef, useState } from "react";
import type { MatchClient, TimelineEntry } from "./match-client";
import type { ActionType } from "@cc/protocol";
import type { Dict, Locale } from "./i18n";
import { actionName, describeEntry, personaName, reasonText } from "./i18n";
import { formatClock, slotTheme } from "./theme";
import { WhyPanel } from "./WhyPanel";

/**
 * A live decision timeline for one cricket.
 *
 * `003.md` §5 asks for the two crickets' reasoning to be readable *side by side
 * during* the match rather than merged, because the drama in this game is
 * comparative: one cricket scouts patiently while the other gambles on bait, and
 * that contrast is invisible in a single interleaved list. The merged story is
 * the post-match Battle Timeline instead.
 *
 * This renders a single column rather than both, so the caller can place it in a
 * gutter beside the broadcast: each column belongs on its cricket's own side of
 * the screen, which is the spatial cue that tells a viewer whose reasoning they
 * are reading without needing to check a header.
 *
 * Ordering is oldest-first here, unlike a chat log. A timeline you read *while*
 * it happens should grow downward and stay still: a reversed list reflows every
 * row on every decision, which makes it unreadable at 4× speed. The body sticks
 * to the bottom so the newest action stays in view unless the viewer scrolls up.
 */
export function SplitTimelines({
  slot,
  client,
  locale,
  d,
}: {
  /** Which cricket's decisions to show. */
  slot: 0 | 1;
  client: MatchClient;
  locale: Locale;
  d: Dict;
}) {
  const [selected, setSelected] = useState<TimelineEntry | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const stickToLatest = useRef(true);
  const entries = client.timeline;
  const entryCount = entries.length;

  // Keyed on `entryCount`, not on `entries`. The client mutates its timeline in
  // place with `push`, so the array *reference* never changes and keying on it
  // would freeze the column at whatever it held on first render — it would sit at
  // zero decisions for the whole match. The reference does change when the
  // timeline is trimmed, which made that bug intermittent rather than obvious.
  const rows = useMemo(() => {
    const mine: TimelineEntry[] = [];
    for (const entry of entries) if (entry.slot === slot) mine.push(entry);
    return mine.slice(-90);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryCount, slot]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !stickToLatest.current) return;
    el.scrollTop = el.scrollHeight;
  }, [rows.length, rows[rows.length - 1]?.id]);

  const theme = slotTheme(slot);
  const id = client.infos[slot].id;
  const who = personaName(client.personaOf(id), locale);

  return (
    <div className="panel split-col" style={{ minHeight: 0 }}>
      <div className="panel-head" style={{ borderColor: theme.color }}>
        <span style={{ color: theme.color }}>{who}</span>
        <span className="mono" style={{ color: "var(--dimmer)", fontSize: 9 }}>
          {slot === 0 ? "A" : "B"} · {rows.length}
        </span>
      </div>
      <div
        className="panel-body"
        ref={bodyRef}
        onScroll={() => {
          const el = bodyRef.current;
          if (!el) return;
          // Leave a little slack so a sub-pixel bounce does not unlock stickiness.
          stickToLatest.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
      >
        {rows.length === 0 ? (
          <div className="empty">{d.noDecisions}</div>
        ) : (
          <div className="timeline">
            {rows.map((entry) => (
              <TimelineRow
                key={entry.id}
                entry={entry}
                client={client}
                locale={locale}
                d={d}
                selected={selected}
                onSelect={setSelected}
                compact
              />
            ))}
          </div>
        )}
      </div>
      {selected && selected.slot === slot ? (
        <WhyPanel
          entry={selected}
          client={client}
          locale={locale}
          d={d}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The merged post-match story.
 *
 * Newest first, because a finished match is something you *look back* on: the
 * last decision is usually the decisive one, so it should be at the top rather
 * than 300 rows down.
 */
export function BattleTimeline({
  client,
  locale,
  d,
}: {
  client: MatchClient;
  locale: Locale;
  d: Dict;
}) {
  const [selected, setSelected] = useState<TimelineEntry | null>(null);
  const entries = [...client.timeline].reverse();
  const keyCount = client.timeline.filter((e) => e.isKey).length;

  return (
    <div id="battle-timeline" className="panel" style={{ flex: 1, minHeight: 0 }}>
      <div className="panel-head">
        <span>{d.battleTimeline}</span>
        <span className="mono" style={{ color: "var(--dimmer)", fontSize: 9 }}>
          {d.movesCount(client.timeline.length)}
          {keyCount > 0 ? ` · ${d.keyCount(keyCount)}` : ""}
        </span>
      </div>
      <div className="panel-body">
        {selected ? (
          <WhyPanel
            entry={selected}
            client={client}
            locale={locale}
            d={d}
            onClose={() => setSelected(null)}
          />
        ) : null}
        {entries.length === 0 ? (
          <div className="empty">{d.waitingFirstDecision}</div>
        ) : (
          <div className="timeline">
            {entries.slice(0, 160).map((entry) => (
              <TimelineRow
                key={entry.id}
                entry={entry}
                client={client}
                locale={locale}
                d={d}
                selected={selected}
                onSelect={setSelected}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineRow({
  entry,
  client,
  locale,
  d,
  selected,
  onSelect,
  compact,
}: {
  entry: TimelineEntry;
  client: MatchClient;
  locale: Locale;
  d: Dict;
  selected: TimelineEntry | null;
  onSelect: (entry: TimelineEntry | null) => void;
  compact?: boolean;
}) {
  const theme = slotTheme(entry.slot);
  const age = client.tick - entry.tick;
  const who = personaName(client.personaOf(entry.agentId), locale);
  const isSelected = selected?.id === entry.id;

  return (
    <div
      className={`tl-row tl-slot-${entry.slot} ${entry.isKey ? "is-key" : ""}${
        isSelected ? " is-selected" : ""
      }`}
      style={{ opacity: age < 12 ? 1 : Math.max(0.42, 1 - age / 400) }}
      onClick={() => onSelect(isSelected ? null : entry)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect(isSelected ? null : entry);
      }}
    >
      <div className="tl-time">{formatClock(entry.tick)}</div>
      <div className="tl-main">
        {compact ? null : (
          <div className="tl-who" style={{ color: theme.color }}>
            {who}
          </div>
        )}
        <div className="tl-text">
          <span className="tl-icon">{entry.icon}</span>
          {entry.kind === "decision" ? (
            <>
              <strong>{actionName((entry.action ?? "WAIT") as ActionType, locale)}</strong>
              {entry.reason ? (
                <span className="reason"> — {reasonText(entry.reason, locale)}</span>
              ) : null}
            </>
          ) : (
            describeEntry(entry, who, locale, d)
          )}
        </div>
      </div>
    </div>
  );
}
